/**
 * ThreadListSidebar — chat history sidebar for the full-page AI chat.
 *
 * All mutations are optimistic: the UI updates instantly and Firestore
 * syncs in the background. Thread switching resets the chat runtime via
 * the provider's `switchThread` / `chatSessionKey` mechanism.
 */
import { type FC, useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Archive,
  Trash2,
  Check,
  X,
  Search,
  Loader2,
  Pin,
  Download,
  GitBranch,
} from 'lucide-react';
import { threadApi, type ChatThread } from './adapters/firestore-thread-adapter';
import { reportSurgeryFailure } from './threadSurgery';
import { useThreadState } from './AiChatProvider';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/Toast';

interface ThreadListSidebarProps {
  className?: string;
}

export const ThreadListSidebar: FC<ThreadListSidebarProps> = ({ className = '' }) => {
  const { currentThreadId, switchThread, threadVersion } = useThreadState();
  const { user, loading: authLoading } = useAuth();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const loadThreads = useCallback(async (activeThreadId?: string | null) => {
    try {
      const res = await threadApi.list();
      let list = res.threads ?? [];

      if (activeThreadId && !list.some((t) => t.id === activeThreadId)) {
        try {
          const missing = await threadApi.get(activeThreadId);
          if (missing && missing.status === 'regular') {
            list = [missing, ...list];
          }
        } catch {
          // Thread may have been deleted or not yet created
        }
      }

      setThreads(list);
      setHasMore(res.hasMore ?? false);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      console.error('[ThreadList] Failed to load threads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await threadApi.list(nextCursor);
      setThreads((prev) => [...prev, ...(res.threads ?? [])]);
      setHasMore(res.hasMore ?? false);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      console.error('[ThreadList] Failed to load more threads:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  useEffect(() => {
    if (authLoading) return;
    loadThreads(currentThreadId);
  }, [loadThreads, threadVersion, authLoading, user, currentThreadId]);

  // ── New Chat (local reset only — thread created on first message) ────────

  const handleNewChat = () => {
    if (currentThreadId === null) return;
    switchThread(null);
  };

  // ── Optimistic Rename ────────────────────────────────────────────────────

  const handleRename = (id: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;

    const prev = threads.find((t) => t.id === id);
    setThreads((ts) =>
      ts.map((t) => (t.id === id ? { ...t, title: trimmed } : t)),
    );
    setRenamingId(null);

    threadApi.update(id, { title: trimmed }).catch((err) => {
      console.error('[ThreadList] Rename sync failed:', err);
      if (prev) setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, title: prev.title } : t)));
    });
  };

  // ── Optimistic Archive ───────────────────────────────────────────────────

  const handleArchive = (id: string) => {
    setThreads((ts) =>
      ts.map((t) => (t.id === id ? { ...t, status: 'archived' as const } : t)),
    );
    setMenuOpenId(null);
    if (currentThreadId === id) switchThread(null);

    threadApi.update(id, { status: 'archived' }).catch((err) => {
      console.error('[ThreadList] Archive sync failed:', err);
      setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'regular' as const } : t)));
    });
  };

  // ── Optimistic Pin/Unpin ────────────────────────────────────────────────

  const handleTogglePin = (id: string) => {
    const thread = threads.find((t) => t.id === id);
    const newPinned = !(thread?.pinned ?? false);
    setThreads((ts) =>
      ts.map((t) => (t.id === id ? { ...t, pinned: newPinned } : t)),
    );
    setMenuOpenId(null);

    threadApi.update(id, { pinned: newPinned }).catch((err) => {
      console.error('[ThreadList] Pin sync failed:', err);
      setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, pinned: !newPinned } : t)));
    });
  };

  // ── Optimistic Delete ────────────────────────────────────────────────────

  const handleDelete = (id: string) => {
    const snapshot = threads;
    setThreads((ts) => ts.filter((t) => t.id !== id));
    setMenuOpenId(null);
    if (currentThreadId === id) switchThread(null);

    threadApi.delete(id).catch((err) => {
      console.error('[ThreadList] Delete sync failed:', err);
      setThreads(snapshot);
    });
  };

  // ── Export Chat ─────────────────────────────────────────────────────────

  const handleExport = async (id: string) => {
    setMenuOpenId(null);
    try {
      const thread = await threadApi.get(id);
      const messages = (thread.messages ?? []) as Array<{
        role: string;
        parts?: Array<{ type: string; text?: string }>;
      }>;
      let md = `# ${thread.title}\n\n`;
      for (const msg of messages) {
        md += `## ${msg.role === 'user' ? 'You' : 'AI Assistant'}\n\n`;
        for (const part of msg.parts ?? []) {
          if (part.type === 'text' && part.text) md += part.text + '\n\n';
        }
      }
      const blob = new Blob([md], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${thread.title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ThreadList] Export failed:', err);
    }
  };

  // ── Duplicate Chat (fork at the last message into a new thread) ──────────

  const handleDuplicate = async (id: string) => {
    setMenuOpenId(null);
    try {
      const thread = await threadApi.get(id);
      const messages = (thread.messages ?? []) as Array<{ id?: string; role?: string }>;
      // Fork at the last USER message (fork points are user-only server-side).
      let lastId: string | undefined;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i]?.role === 'user' && messages[i]?.id) { lastId = messages[i].id; break; }
      }
      if (!lastId) {
        toast.error('Nothing to duplicate — this chat has no message to branch from.');
        return;
      }
      const res = await threadApi.fork(id, { messageId: lastId });
      switchThread(res.id);
    } catch (err) {
      reportSurgeryFailure(err, {
        threadId: id,
        fallback: 'Duplicate failed — no copy was created',
        retry: () => void handleDuplicate(id),
      });
    }
  };

  // ── Close menu on outside click ──────────────────────────────────────────

  useEffect(() => {
    if (!menuOpenId) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  const filtered = threads
    .filter(
      (t) =>
        t.status === 'regular' &&
        (!search || t.title.toLowerCase().includes(search.toLowerCase())),
    )
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return 0;
    });

  return (
    <div className={`flex flex-col h-full bg-white border-r border-zinc-200 ${className}`}>
      {/* Header */}
      <div className="h-12 px-3 flex items-center justify-between border-b border-zinc-200">
        <h2 className="pl-1 text-sm font-medium text-zinc-900">Chats</h2>
        <button
          onClick={handleNewChat}
          title="New chat"
          className="flex items-center gap-1 h-7 px-2 rounded-md text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          New
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <input
            type="text"
            placeholder="Search chats..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-zinc-200 bg-white text-xs text-zinc-700 placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-zinc-300 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-8 h-8 text-zinc-200 mx-auto mb-2" />
            <p className="text-xs text-zinc-400">
              {search ? 'No matching chats' : 'No chats yet'}
            </p>
          </div>
        ) : (
          <>
            <AnimatePresence>
              {filtered.map((thread) => (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  isActive={thread.id === currentThreadId}
                  isMenuOpen={menuOpenId === thread.id}
                  isRenaming={renamingId === thread.id}
                  renameValue={renameValue}
                  onSelect={() => {
                    if (thread.id !== currentThreadId) switchThread(thread.id);
                  }}
                  onMenuToggle={() =>
                    setMenuOpenId(menuOpenId === thread.id ? null : thread.id)
                  }
                  onRenameStart={() => {
                    setRenamingId(thread.id);
                    setRenameValue(thread.title);
                    setMenuOpenId(null);
                  }}
                  onRenameChange={setRenameValue}
                  onRenameConfirm={() => handleRename(thread.id)}
                  onRenameCancel={() => setRenamingId(null)}
                  onArchive={() => handleArchive(thread.id)}
                  onDelete={() => handleDelete(thread.id)}
                  onTogglePin={() => handleTogglePin(thread.id)}
                  onExport={() => handleExport(thread.id)}
                  onDuplicate={() => handleDuplicate(thread.id)}
                />
              ))}
            </AnimatePresence>
            {hasMore && !search && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mt-1 rounded-md text-xs text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700 transition-colors disabled:opacity-50"
              >
                {loadingMore ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span>Load older chats</span>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Thread Item ─────────────────────────────────────────────────────────────

interface ThreadItemProps {
  thread: ChatThread;
  isActive: boolean;
  isMenuOpen: boolean;
  isRenaming: boolean;
  renameValue: string;
  onSelect: () => void;
  onMenuToggle: () => void;
  onRenameStart: () => void;
  onRenameChange: (v: string) => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onExport: () => void;
  onDuplicate: () => void;
}

function ThreadItem({
  thread,
  isActive,
  isMenuOpen,
  isRenaming,
  renameValue,
  onSelect,
  onMenuToggle,
  onRenameStart,
  onRenameChange,
  onRenameConfirm,
  onRenameCancel,
  onArchive,
  onDelete,
  onTogglePin,
  onExport,
  onDuplicate,
}: ThreadItemProps) {
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming) renameInputRef.current?.focus();
  }, [isRenaming]);

  const timeAgo = formatTimeAgo(thread.updatedAt);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -8 }}
      className="relative mb-0.5"
    >
      <button
        onClick={onSelect}
        className={`w-full group flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-left transition-colors ${
          isActive ? 'bg-zinc-100' : 'hover:bg-zinc-50'
        }`}
      >
        {thread.pinned ? (
          <Pin className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
        ) : (
          <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-zinc-600' : 'text-zinc-400'}`} />
        )}
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={renameInputRef}
                type="text"
                value={renameValue}
                onChange={(e) => onRenameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onRenameConfirm();
                  if (e.key === 'Escape') onRenameCancel();
                }}
                className="flex-1 text-xs bg-white border border-zinc-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
              <button
                onClick={onRenameConfirm}
                className="text-zinc-500 hover:text-zinc-900"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={onRenameCancel}
                className="text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <>
              <div
                className={`text-meta truncate ${
                  isActive ? 'text-zinc-900' : 'text-zinc-700'
                }`}
              >
                {thread.title}
              </div>
              <div className="text-xs text-zinc-400">{timeAgo}</div>
            </>
          )}
        </div>

        {!isRenaming && (
          <div
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle();
            }}
          >
            <MoreHorizontal className="w-3.5 h-3.5 text-zinc-400 hover:text-zinc-600" />
          </div>
        )}
      </button>

      {/* Context Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className="absolute right-2 top-full mt-1 z-20 w-36 bg-white rounded-lg border border-zinc-200 shadow-lg p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onTogglePin}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Pin className="w-3 h-3" />
              {thread.pinned ? 'Unpin' : 'Pin to top'}
            </button>
            <button
              onClick={onRenameStart}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Pencil className="w-3 h-3" />
              Rename
            </button>
            <button
              onClick={onDuplicate}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <GitBranch className="w-3 h-3" />
              Duplicate
            </button>
            <button
              onClick={onExport}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Download className="w-3 h-3" />
              Export
            </button>
            <button
              onClick={onArchive}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Archive className="w-3 h-3" />
              Archive
            </button>
            <div className="my-1 border-t border-zinc-100" />
            <button
              onClick={onDelete}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Delete
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;

  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
